import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  makeStyles,
  CircularProgress,
  Backdrop,
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import { useUserClubMembership } from 'spypoint/src/user-module/club-membership/core/useUserClubMembership'
import JoinTheClubDialog from 'spypoint/src/user-module/club-membership/ui/join-the-club-dialog/JoinTheClubDialog'
import useConfirmationDialogActions from 'shared-module/components/confirmation/useConfirmationDialogActions'
import useFilterCollection from 'photo-module/filters-bar/core/filters.hooks'
import PrimaryButton from 'shared-module/new-components/buttons/PrimaryButton'
import SecondaryButton from 'shared-module/new-components/buttons/SecondaryButton'
import usePhotoTagging from '../core/usePhotoTagging'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#313030',
    flex: 1,
    height: 'calc(100% - 70px)',
    marginBottom: '20px',
    [theme.breakpoints.down('sm')]: {
      position: 'fixed !important',
      top: '0 !important',
      left: '0 !important',
      right: '0 !important',
      bottom: '0 !important',
      height: '100vh !important',
      width: '100vw !important',
      marginBottom: '0 !important',
      zIndex: '4000 !important', // Increased z-index
      isolation: 'isolate',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: '24px 24px 0 24px',
    gap: '14px',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 16px 0 16px',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  closeButton: {
    color: '#939090',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(147, 144, 144, 0.1)',
      color: '#F5F4F4',
    },
  },
  title: {
    fontFamily: 'Oswald-regular',
    fontSize: '21px',
    fontWeight: '500',
    color: '#F5F4F4',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  titleBadge: {
    backgroundColor: '#F89021',
    color: '#000',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '16px',
    fontWeight: '700',
    minWidth: '20px',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  titleBadgeHidden: {
    display: 'none',
  },
  titleCount: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  sectionTitle: {
    fontFamily: 'ibm-plex-sans-regular',
    fontSize: '14px',
    fontWeight: 400,
    color: '#FBF9F9',
  },
  countText: {
    fontFamily: 'ibm-plex-sans-regular',
    fontSize: '17px',
    color: '#F89021',
    fontWeight: 'normal',
  },
  speciesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flex: 1,
    overflowY: 'auto',
    scrollbarWidth: 'none', // Firefox
    '&::-webkit-scrollbar': {
      display: 'none', // Chrome, Safari, Edge
    },
  },
  speciesItem: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '27px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '16px',
      paddingRight: '0px',
    },
    '& .MuiFormControlLabel-root': {
      margin: 0,
      width: '272px',
      height: '43px',
      flexDirection: 'row-reverse',
      justifyContent: 'space-between',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        paddingRight: '5px',
      },
    },
    '& .MuiCheckbox-root': {
      color: '#939090',
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        color: '#464444',
      },
    },
    '& .MuiFormControlLabel-label': {
      fontFamily: 'ibm-plex-sans-regular; !important',
      fontSize: '14px',
      color: '#F5F4F4',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      '&.Mui-disabled': {
        color: '#767474',
      },
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '19px 24px',
    borderTop: '1px solid #464444',
    backgroundColor: '#313030',
    [theme.breakpoints.down('sm')]: {
      padding: '16px',
      gap: theme.spacing(1.5),
      marginBottom: '5px',
    },
    '& button': {
      width: '132px',
      height: '36px',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
        maxWidth: 'none',
      },
    },
  },
  emptyState: {
    textAlign: 'center',
    color: '#939090',
    fontStyle: 'italic',
    marginTop: theme.spacing(2),
    fontFamily: 'IBMPlexSans-Regular',
    fontSize: '14px',
  },
  nonMemberSection: {
    padding: '14px 20px',
    backgroundColor: '#1F1E1E',
    borderRadius: 8,
    border: '1px solid rgba(255, 193, 7, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  nonMemberText: {
    color: '#D3CFCF',
    fontFamily: 'IBMPlexSans-Regular',
    fontSize: '14px',
    textAlign: 'center',
  },
  learnMoreButton: {
    minWidth: '116px',
    height: '42px',
    borderRadius: '6px',
    marginTop: '12.5px',
  },
  loadingOverlay: {
    zIndex: '5000 !important',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  loadingText: {
    color: '#F5F4F4',
    fontFamily: 'IBMPlexSans-Regular',
    fontSize: '16px',
    marginTop: '16px',
  },
}))

const SpeciesTaggingModal = ({ currentTags, photo, onClose }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const userMembership = useUserClubMembership()
  const { filterCollection } = useFilterCollection()
  const { updatePhotoTags, isUpdating } = usePhotoTagging()
  const dialogActions = useConfirmationDialogActions()

  const [selectedTags, setSelectedTags] = useState(() => {
    return currentTags ? [...currentTags] : []
  })

  const availableSpecies = useMemo(() => {
    const species = filterCollection?.species || []

    return species.sort((a, b) => {
      if (a.nameId === 'buck') return -1
      if (b.nameId === 'buck') return 1
      return 0
    })
  }, [filterCollection])

  const handleSpeciesToggle = (speciesNameId) => {
    if (!userMembership.filters.includes(speciesNameId)) {
      dialogActions.open()
      return
    }

    setSelectedTags(prev => {
      const isSelected = prev.includes(speciesNameId)
      if (isSelected) {
        return prev.filter(tag => tag !== speciesNameId)
      } else {
        return [...prev, speciesNameId]
      }
    })
  }

  const handleSave = async () => {
    const updatedTags = {
      mediaIds: [photo.id],
      tags: {
        species: selectedTags,
        // timeOfDay: photo.tags?.filter(tag => filterCollection.timeOfDay?.includes(tag)) || [], // Will be used on the future
      },
    }

    try {
      const result = await updatePhotoTags(updatedTags)
      if (result.success) {
        onClose()
      }
    } catch (error) {
      console.error('Failed to update photo tags:', error)
    }
  }

  const handleClearAll = () => {
    setSelectedTags([])
  }

  const handleLearnMore = () => {
    dialogActions.open()
  }

  const isSpeciesAvailable = (speciesNameId) => {
    return userMembership.filters.includes(speciesNameId)
  }

  const hasChanges = useMemo(() => {
    const originalTags = currentTags || []

    if (originalTags.length !== selectedTags.length) return true

    if (originalTags.some(tag => !selectedTags.includes(tag))) return true

    if (selectedTags.some(tag => !originalTags.includes(tag))) return true

    return false
  }, [currentTags, selectedTags])

  return (
    <Box className={classes.container} data-testid="species-tagging-modal">
      <Box className={classes.content}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            { t('app:filters.tags.custom_tags.title').toUpperCase() }
            <span className={classes.titleCount}>({ selectedTags.length })</span>
            <span className={`${classes.titleBadge} ${selectedTags.length === 0 ? classes.titleBadgeHidden : ''}`}>
              { selectedTags.length }
            </span>
          </Typography>
          <IconButton
            className={classes.closeButton}
            onClick={onClose}
            disabled={isUpdating}
            aria-label={t('button.close')}
            data-testid="close-modal-button"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography className={classes.sectionTitle}>
          { t('app:filters.tags.species_tagging.species_title').toUpperCase() } <Typography component="span" variant="subtitle1" className={classes.countText}>({ selectedTags.length })</Typography>
        </Typography>

        <Box className={classes.speciesList}>
          { availableSpecies.length > 0
            ? (
                availableSpecies.map((species, index) => {
                  const isSelected = selectedTags.includes(species.nameId)
                  const isAvailable = isSpeciesAvailable(species.nameId)

                  return (
                    <React.Fragment key={species.nameId}>
                      <Box className={classes.speciesItem}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSpeciesToggle(species.nameId)}
                              disabled={!isAvailable || isUpdating}
                            />
                          )}
                          label={t(`bucktracker.species.${species.nameId}`)}
                        />
                      </Box>
                      { species.nameId === 'buck' && !userMembership.isMember && (
                        <Box className={classes.nonMemberSection}>
                          <Typography className={classes.nonMemberText} variant="body2">
                            { t('app:filters.tags.species_tagging.non_member_notice') }
                          </Typography>
                          <SecondaryButton
                            className={classes.learnMoreButton}
                            onClick={handleLearnMore}
                          >
                            { t('app:filters.tags.species_tagging.learn_more') }
                          </SecondaryButton>
                        </Box>
                      ) }
                    </React.Fragment>
                  )
                })
              )
            : (
              <Typography className={classes.emptyState}>
                { t('app:filters.tags.species_tagging.no_species_available') }
              </Typography>
              ) }
        </Box>
      </Box>

      <Box className={classes.actions}>
        <SecondaryButton onClick={handleClearAll} disabled={selectedTags.length === 0 || isUpdating}>
          { t('app:filters.tags.species_tagging.clear_all') }
        </SecondaryButton>
        <PrimaryButton
          onClick={handleSave}
          disabled={!hasChanges || isUpdating}
        >
          { isUpdating ? t('app:filters.tags.species_tagging.saving') : t('app:filters.tags.species_tagging.save') }
        </PrimaryButton>
      </Box>

      <JoinTheClubDialog
        open={dialogActions.isOpen}
        onClose={dialogActions.close}
      />

      <Backdrop
        open={isUpdating}
        className={classes.loadingOverlay}
      >
        <CircularProgress
          size={60}
          thickness={4}
          style={{ color: '#F89021' }}
        />
        <Typography className={classes.loadingText}>
          { t('app:filters.tags.species_tagging.saving') }
        </Typography>
      </Backdrop>
    </Box>
  )
}

export default SpeciesTaggingModal
